import { useCallback, useEffect, useRef, useState } from "react";
import { extractionService } from "@/services/extraction";
import { ExtractedApplicationFields } from "@/types/extraction";

type ExtractionStatus = "idle" | "extracting" | "done" | "error";

interface UseApplicationExtractionReturn {
  status: ExtractionStatus;
  fileName: string;
  errorMessage: string;
  showSlowMessage: boolean;
  handleDrop: (acceptedFiles: File[]) => void;
  reset: () => void;
}

/**
 * Hook that manages MCA application PDF extraction via LlamaCloud.
 * Handles the upload lifecycle: idle → extracting → done/error,
 * including a 15-second slow-response indicator.
 */
export function useApplicationExtraction(
  onFieldsExtracted: (fields: ExtractedApplicationFields) => void
): UseApplicationExtractionReturn {
  const [status, setStatus] = useState<ExtractionStatus>("idle");
  const [fileName, setFileName] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showSlowMessage, setShowSlowMessage] = useState(false);
  const slowTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (slowTimerRef.current) clearTimeout(slowTimerRef.current);
    };
  }, []);

  const handleDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;

      setStatus("extracting");
      setFileName(file.name);
      setErrorMessage("");
      setShowSlowMessage(false);

      slowTimerRef.current = setTimeout(() => setShowSlowMessage(true), 15000);

      try {
        const fields = await extractionService.parseApplication(file);
        setStatus("done");
        onFieldsExtracted(fields);
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : "Extraction failed — please fill in manually";
        setStatus("error");
        setErrorMessage(message);
      } finally {
        if (slowTimerRef.current) {
          clearTimeout(slowTimerRef.current);
          slowTimerRef.current = null;
        }
        setShowSlowMessage(false);
      }
    },
    [onFieldsExtracted]
  );

  const reset = useCallback(() => {
    setStatus("idle");
    setFileName("");
    setErrorMessage("");
    setShowSlowMessage(false);
  }, []);

  return { status, fileName, errorMessage, showSlowMessage, handleDrop, reset };
}
